import { useEffect } from "react";
import { useParams } from "react-router";
import styled from "@emotion/styled";

import { UpdatecampaignAccessButton } from "../../campaign-access-management/components/UpdatecampaignAccessButton";
import { CenteredErrorMessage } from "../../../ui-components/CenteredErrorMessage";
import { CenteredSpinner } from "../../../ui-components/CenteredSpinner";
import { Section as BaseSection } from "../../../share/company/components/ui-share";

import {
  getCampaignAccessData,
  setInitialState,
} from "../../campaign-access-management/store";
import { State } from "../../../utils/store";
import { selectedCampaignSlugSelector } from "../../../share/campaigns/store";

import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import { getCampaignsAccessUsersData } from "../../../helpers/campaignAccess";

import {
  errorNotification,
  successNotification,
} from "../../../ui-components/Notification";
import { getUserList } from "../../../user-management/store";
import { ArchiveCampaignButton } from "../../../dashboard/components/ArchiveCampaignButton";
import { dateFormatter } from "../../../helpers";

export const GoldenBasketCampaignAccessManagementView = () => {
  const dispatch = useAppDispatch();
  const { selectedClient } = useAppSelector((state: State) => state.clients);
  const { campaignAccessData } = useAppSelector(
    (state: State) => state.campaignAccessManagement
  );
  const { error: updateUsersAccessError, success: updateUsersAccessSuccess } =
    useAppSelector(
      (state: State) => state.campaignAccessManagement.updateUsersAccess
    );
  const {
    loading: getCampaignAccessDataLoading,
    error: getCampaignAccessDataError,
  } = useAppSelector(
    (state: State) => state.campaignAccessManagement.getCampaignAccessData
  );

  const { userList } = useAppSelector((state: State) => state.userManagement);

  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const campaign = useAppSelector(selectedCampaignSlugSelector(campaignSlug));

  useEffect(() => {
    return () => {
      dispatch(setInitialState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (updateUsersAccessError) errorNotification(updateUsersAccessError);
  }, [updateUsersAccessError]);

  useEffect(() => {
    if (updateUsersAccessSuccess)
      successNotification("Access modified successfully");
  }, [updateUsersAccessSuccess]);

  useEffect(() => {
    if (!selectedClient || !campaign) return;
    dispatch(getUserList(selectedClient.uuid));
    dispatch(
      getCampaignAccessData({
        clientUUID: selectedClient.uuid,
        campaignUUID: campaign.uuid,
      })
    );
  }, [dispatch, selectedClient, campaign]);

  if (!campaign) {
    return <CenteredErrorMessage>Campaign not found</CenteredErrorMessage>;
  }

  if (getCampaignAccessDataLoading) {
    return <CenteredSpinner />;
  }

  if (getCampaignAccessDataError) {
    return (
      <CenteredErrorMessage>{getCampaignAccessDataError}</CenteredErrorMessage>
    );
  }

  const usersEnabledInCampaign = campaignAccessData[campaign.uuid];
  const campaignCreatorUUID = campaign.creator?.uuid || "";
  const users = getCampaignsAccessUsersData(
    campaignCreatorUUID,
    userList,
    usersEnabledInCampaign
  );

  const formattedPublishedDate =
    campaign?.publishedAt && dateFormatter(campaign.publishedAt.toString());
  const campaignCreator =
    campaign.creator?.displayName ||
    `${campaign.creator?.firstName} ${campaign.creator?.lastName}`;
  return (
    <Content>
      <FlexSection>
        <div>Edit user access</div>
        <UpdatecampaignAccessButton
          campaignName={campaign.name}
          campaignUUID={campaign.uuid}
          users={users}
        />
      </FlexSection>
      <FlexSection>
        <div>Archive campaign</div>
        <ArchiveCampaignButton
          clientSlug={selectedClient?.slug}
          campaign={campaign}
        />
      </FlexSection>
      <Section>
        <div>Campaign information</div>
        <CampaignInformation>
          {formattedPublishedDate && (
            <div>Published at: {formattedPublishedDate}</div>
          )}
          <div>Created by: {campaignCreator}</div>
          <div>Last edited by: {campaign.lastEditedBy}</div>
        </CampaignInformation>
      </Section>
    </Content>
  );
};

const Content = styled.div`
  width: 100%;
`;

const FlexSection = styled(BaseSection)`
  width: 100%;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Section = styled(BaseSection)`
  width: 100%;
  padding: 1rem;
`;

const CampaignInformation = styled.div`
  padding-left: 2rem;
  padding-top: 0.5rem;
`;
